import {
	BufferAttribute,
	BufferGeometry,
	DirectionalLight,
	LineBasicMaterial,
	LineSegments,
	Mesh,
	MeshStandardMaterial,
	Scene,
	SphereGeometry
} from 'three';

import {
	DirectionalLightHelperObjects,
	getQualitySegments,
	LIGHT_HELPER_DEFAULTS,
	RenderQuality
} from './light-helper.types';

/**
 * Creates visualization objects for a DirectionalLight (sphere + direction line)
 *
 * @param light The DirectionalLight to visualize
 * @param scene Scene to add objects to
 * @param sphereRadius Radius of the sphere
 * @param visible Initial visibility state
 * @param quality Render quality 0-10
 * @returns DirectionalLight helper objects
 */
export const createDirectionalLightVisualization = (
	light: DirectionalLight,
	scene: Scene,
	sphereRadius: number = LIGHT_HELPER_DEFAULTS.CONE_RADIUS,
	visible: boolean = false,
	quality: RenderQuality = LIGHT_HELPER_DEFAULTS.QUALITY
): DirectionalLightHelperObjects => {
	const isLightOn = light.visible && light.intensity > 0;
	const lightColor = light.color.getHex();
	const segments = getQualitySegments(quality);

	// Create sphere at light position
	const sphereGeometry = new SphereGeometry(sphereRadius, segments.radial, segments.radial);
	const sphereColor = isLightOn
		? LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_ON
		: LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF;
	const sphereMaterial = new MeshStandardMaterial({ color: sphereColor, wireframe: false }); // Interface objects never wireframe
	const sphere = new Mesh(sphereGeometry, sphereMaterial);
	sphere.name = `directionalLightSphere_${light.uuid}`;
	sphere.position.copy(light.position);
	sphere.visible = visible;
	// Mark as interface object to exclude from wireframe mode
	sphere.userData.isInterface = true;
	sphere.userData.type = 'helper';
	scene.add(sphere);

	// Create line from light to target
	const lineGeometry = new BufferGeometry();
	const linePositions = new Float32Array([
		light.position.x,
		light.position.y,
		light.position.z,
		light.target.position.x,
		light.target.position.y,
		light.target.position.z
	]);
	lineGeometry.setAttribute('position', new BufferAttribute(linePositions, 3));
	const lineMaterial = new LineBasicMaterial({ color: lightColor });
	const line = new LineSegments(lineGeometry, lineMaterial);
	line.name = `directionalLightLine_${light.uuid}`;
	// Mark as interface object to exclude from wireframe mode
	line.userData.isInterface = true;
	line.userData.type = 'helper';
	line.visible = visible && isLightOn;
	scene.add(line);

	return { sphere, line };
};

/**
 * Updates DirectionalLight visualization objects
 */
export const updateDirectionalLightVisualization = (
	light: DirectionalLight,
	objects: DirectionalLightHelperObjects,
	visible: boolean
): void => {
	const isLightOn = light.visible && light.intensity > 0;
	const lightColor = light.color.getHex();

	// Update sphere position
	objects.sphere.position.copy(light.position);
	objects.sphere.visible = visible;

	// Update sphere color
	const sphereMaterial = objects.sphere.material as MeshStandardMaterial;
	sphereMaterial.color.setHex(
		isLightOn ? LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_ON : LIGHT_HELPER_DEFAULTS.SPHERE_COLOR_OFF
	);

	// Update line
	const linePositions = objects.line.geometry.attributes.position as BufferAttribute;
	linePositions.setXYZ(0, light.position.x, light.position.y, light.position.z);
	linePositions.setXYZ(1, light.target.position.x, light.target.position.y, light.target.position.z);
	linePositions.needsUpdate = true;
	objects.line.visible = visible && isLightOn;

	// Update line color
	const lineMaterial = objects.line.material as LineBasicMaterial;
	lineMaterial.color.setHex(lightColor);
};

/**
 * Disposes DirectionalLight visualization objects
 */
export const disposeDirectionalLightVisualization = (
	objects: DirectionalLightHelperObjects,
	scene: Scene
): void => {
	scene.remove(objects.sphere);
	scene.remove(objects.line);

	objects.sphere.geometry.dispose();
	objects.line.geometry.dispose();

	(objects.sphere.material as MeshStandardMaterial).dispose();
	(objects.line.material as LineBasicMaterial).dispose();
};

